import { useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { t } from '../lib/i18n';
import { messageFor } from '../lib/errors';
import { formatRelative } from '../ui/format';
import { LIFECYCLE_STAGES, updateRecord, type CrmEntity, type LifecycleStage } from './detail-api';

/**
 * Where this account is on the journey.
 *
 * Drawn as the ladder rather than as a dropdown, because the useful information is not which stage
 * they are on — it is how far along that is and what comes next. `OTHER` sits apart from the ladder
 * for the same reason it does on the server: it is not a position, it is a statement that this
 * record is not climbing.
 *
 * Clicking a stage moves them there, in either direction. Automation only ever moves people forward
 * — a customer who fills in an e-book form has not stopped being a customer — but a person
 * correcting what a rule got wrong is the whole reason the correction exists.
 */

const LADDER: LifecycleStage[] = LIFECYCLE_STAGES.filter((stage) => stage !== 'OTHER');

export function LifecyclePanel({
  entity,
  id,
  stage,
  changedAt,
}: {
  entity: CrmEntity;
  id: string;
  stage: LifecycleStage;
  changedAt: string | null;
}): React.JSX.Element {
  const client = useQueryClient();
  const [error, setError] = useState<string | null>(null);

  const move = useMutation({
    mutationFn: (next: LifecycleStage) => updateRecord(entity, id, { lifecycleStage: next }),
    onSuccess: () => {
      setError(null);
      void client.invalidateQueries({ queryKey: ['record', entity, id] });
      void client.invalidateQueries({ queryKey: ['timeline', entity, id] });
    },
    onError: (caught: unknown) => setError(messageFor(caught)),
  });

  const current = LADDER.indexOf(stage);

  return (
    <div className="lifecycle">
      <div className="lifecycle__ladder" role="group" aria-label={t('crm.lifecycle')}>
        {LADDER.map((entry, index) => (
          <button
            key={entry}
            type="button"
            className={[
              'lifecycle__step',
              index === current ? 'is-current' : '',
              current >= 0 && index < current ? 'is-done' : '',
            ]
              .filter(Boolean)
              .join(' ')}
            disabled={move.isPending}
            onClick={() => move.mutate(entry)}
          >
            {t(`crm.stage.${entry}`)}
          </button>
        ))}
      </div>

      <div className="cell-inline">
        <button
          type="button"
          className={`chip${stage === 'OTHER' ? ' chip--active' : ''}`}
          disabled={move.isPending}
          onClick={() => move.mutate('OTHER')}
        >
          {t('crm.stage.OTHER')}
        </button>

        {changedAt && <span className="muted small">{formatRelative(changedAt)}</span>}
      </div>

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
